<template>
  <div class="projects">
    <div class="container">
      <h2 class="section-title">{{ $t('projects.title') }}</h2>
      
      <div class="filters">
        <div class="search">
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="$t('projects.searchPlaceholder')"
            class="search-input"
          />
        </div>
        
        <div class="tags-filter">
          <button 
            class="tag-btn" 
            :class="{ active: selectedTag === '' }" 
            @click="selectedTag = ''"
          >
            {{ $t('projects.allTags') }}
          </button>
          <button 
            v-for="tag in uniqueTags" 
            :key="tag" 
            class="tag-btn" 
            :class="{ active: selectedTag === tag }" 
            @click="selectedTag = tag"
          >
            {{ tag }}
          </button>
        </div>
      </div>
      
      <div v-if="filteredProjects.length" class="projects-grid">
        <ProjectCard 
          v-for="project in filteredProjects" 
          :key="project.id" 
          :project="project" 
        />
      </div>
      
      <div v-else class="no-results">
        <p>{{ $t('projects.noResults') }}</p>
        <button class="btn btn-outline" @click="resetFilters">{{ $t('projects.resetFilters') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import ProjectCard from '@/components/ProjectCard.vue'
import { useProjectsStore } from '@/stores/projects'

export default defineComponent({
  name: 'ProjectsView',
  components: {
    ProjectCard
  },
  data() {
    return {
      searchQuery: '',
      selectedTag: ''
    }
  },
  computed: {
    projects() {
      const projectsStore = useProjectsStore()
      return projectsStore.getProjects
    },
    uniqueTags() {
      const allTags = this.projects.flatMap(project => project.tags)
      return [...new Set(allTags)].sort()
    },
    filteredProjects() {
      return this.projects.filter(project => {
        // Filter by search query
        const matchesSearch = this.searchQuery === '' || 
          project.title.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          project.description.toLowerCase().includes(this.searchQuery.toLowerCase())
        
        // Filter by selected tag
        const matchesTag = this.selectedTag === '' || 
          project.tags.includes(this.selectedTag)
        
        return matchesSearch && matchesTag
      })
    }
  },
  methods: {
    resetFilters() {
      this.searchQuery = ''
      this.selectedTag = ''
    }
  }
})
</script>

<style lang="scss" scoped>
.projects {
  padding: 1rem 0;
}

.section-title {
  margin-bottom: 2rem;
  text-align: center;
  font-size: 2.5rem;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: var(--color-primary);
    margin: 1rem auto 0;
  }
}

.filters {
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
}

.search {
  flex: 1;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background-color: var(--color-card-bg);
  color: var(--color-text);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb, 248, 198, 48), 0.2);
  }
  
  &::placeholder {
    color: var(--color-text);
    opacity: 0.6;
  }
}

.tags-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tag-btn {
  background-color: var(--color-card-bg);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: var(--color-text);
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  
  &.active {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.no-results {
  text-align: center;
  padding: 3rem 0;
  
  p {
    margin-bottom: 1.5rem;
    font-size: 1.125rem;
    opacity: 0.8;
  }
}
</style>